import React, { useEffect, useState } from 'react';
import { makeStyles } from 'tss-react/mui';
import {
  Dialog,
  Button,
  Typography,
  IconButton,
  TextField,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import {
  BUILTIN_AVATAR_PALETTE,
  DEFAULT_BUILTIN_COLOR,
  normalizeBuiltinColor,
  builtinAvatarDataUrl,
  parseBuiltinAvatarSpec,
  pickableBuiltinAvatarIds,
} from '../services/builtinAvatars';

const useStyles = makeStyles()((theme) => ({
  paper: {
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 10,
    width: 380,
    maxWidth: '92vw',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(1.5, 2),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  title: {
    fontSize: 15,
    fontWeight: 600,
    color: theme.palette.text.primary,
  },
  body: {
    padding: theme.spacing(2),
  },
  // Live preview strip: the selected avatar next to the username rendered in
  // the selected color — mirrors the legacy AvatarPreview (avatar + name).
  preview: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    padding: theme.spacing(1, 1.5),
    marginBottom: theme.spacing(2),
    backgroundColor: theme.palette.custom.panel,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
  },
  previewName: {
    fontSize: 15,
    fontWeight: 600,
  },
  sectionLabel: {
    fontSize: 12,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(1),
  },
  avatarGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(6, 1fr)',
    gap: 4,
    marginBottom: theme.spacing(2),
  },
  avatarTile: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 3,
    background: 'none',
    border: '2px solid transparent',
    borderRadius: 6,
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: theme.palette.custom.hoverOverlay,
    },
  },
  avatarTileSelected: {
    borderColor: theme.palette.primary.main,
    backgroundColor: theme.palette.custom.accentWash,
  },
  colorGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(10, 1fr)',
    gap: 3,
    marginBottom: theme.spacing(1.5),
  },
  swatch: {
    width: '100%',
    aspectRatio: '1 / 1',
    border: '2px solid transparent',
    borderRadius: 4,
    cursor: 'pointer',
    padding: 0,
    '&:hover': {
      transform: 'scale(1.15)',
    },
  },
  swatchSelected: {
    borderColor: theme.palette.common.white,
    boxShadow: `0 0 0 1px ${theme.palette.text.primary}`,
  },
  hexRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  hexLabel: {
    fontSize: 13,
    color: theme.palette.text.secondary,
  },
  hexInput: {
    width: 92,
    '& input': {
      fontFamily: 'monospace',
      textTransform: 'uppercase',
    },
  },
  hexError: {
    fontSize: 12,
    color: theme.palette.error.main,
  },
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: theme.spacing(1),
    padding: theme.spacing(1.5, 2),
    borderTop: `1px solid ${theme.palette.divider}`,
  },
  saveBtn: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.custom.accentContrastText,
    '&:hover': { backgroundColor: theme.palette.primary.dark },
  },
}));

/**
 * Avatar & color picker — web remake of the original Flash chat's
 * ColorAvatarPopup. One color is the user's identity: it flat-fills the
 * avatar's silhouette layer AND becomes their username color (the caller
 * couples the two on save, like the legacy Submit dispatching both
 * ChangeColorEvent and ChangeAvatarEvent).
 *
 * Props:
 *  - open, onClose
 *  - username      : shown in the live preview, rendered in the picked color
 *  - initialSpec   : current "builtin:NN:hex" spec (preselects avatar + color)
 *  - initialColor  : fallback starting color when no builtin spec is set
 *                    (e.g. the user's current username color)
 *  - blockedColors : hexes (with '#') the user may not pick — staff-reserved
 *                    username colors; the server enforces this too
 *  - canUseStaffAvatars : ADMIN/OWNER — shows the staff-only cop avatars
 *                    (13/14), prepended like the legacy ShowAdmins
 *  - onSave({ id, hex }) : persist; returns Promise<bool> (true closes)
 */
const AvatarPickerDialog = ({
  open,
  onClose,
  username = 'You',
  initialSpec,
  initialColor,
  blockedColors = [],
  canUseStaffAvatars = false,
  onSave,
}) => {
  const { classes, cx } = useStyles();
  const avatarIds = pickableBuiltinAvatarIds(canUseStaffAvatars);
  const [avatarId, setAvatarId] = useState(avatarIds[0]);
  const [hex, setHex] = useState(DEFAULT_BUILTIN_COLOR);
  const [hexDraft, setHexDraft] = useState(DEFAULT_BUILTIN_COLOR);
  const [hexError, setHexError] = useState('');
  const [saving, setSaving] = useState(false);

  // (Re)seed the selection each time the dialog opens: the current builtin
  // choice if there is one, else the first template in the user's current
  // username color (or the legacy default red). A spec pointing at an avatar
  // this user may not pick (e.g. a staff avatar after a demotion) falls back
  // to the first pickable one.
  useEffect(() => {
    if (!open) return;
    const ids = pickableBuiltinAvatarIds(canUseStaffAvatars);
    const parsed = parseBuiltinAvatarSpec(initialSpec);
    const startHex =
      parsed?.hex || normalizeBuiltinColor(initialColor) || DEFAULT_BUILTIN_COLOR;
    setAvatarId(parsed && ids.includes(parsed.id) ? parsed.id : ids[0]);
    setHex(startHex);
    setHexDraft(startHex);
    setHexError('');
    setSaving(false);
  }, [open, initialSpec, initialColor, canUseStaffAvatars]);

  const isBlocked = (h) =>
    blockedColors.some((c) => c.toLowerCase() === `#${h}`.toLowerCase());

  const pickColor = (h) => {
    const norm = normalizeBuiltinColor(h);
    if (!norm) return;
    if (isBlocked(norm)) {
      setHexError('That color is reserved for staff.');
      return;
    }
    setHexError('');
    setHex(norm);
    setHexDraft(norm);
  };

  // Free hex entry, like the legacy popup's 6-char field: applies as soon as
  // six valid digits are present.
  const handleHexInput = (value) => {
    const clean = value.replace(/[^0-9a-fA-F]/g, '').slice(0, 6).toLowerCase();
    setHexDraft(clean);
    setHexError('');
    if (clean.length === 6) pickColor(clean);
  };

  const handleSave = async () => {
    if (!onSave || saving) return;
    setSaving(true);
    try {
      const ok = await onSave({ id: avatarId, hex });
      if (ok) onClose && onClose();
    } finally {
      setSaving(false);
    }
  };

  // Row-major render of the column-major legacy palette, so each on-screen
  // column keeps the original hue-family grouping (grays, reds, oranges, …).
  const swatchRows = [];
  for (let row = 0; row < 5; row += 1) {
    for (let col = 0; col < BUILTIN_AVATAR_PALETTE.length; col += 1) {
      swatchRows.push(BUILTIN_AVATAR_PALETTE[col][row]);
    }
  }

  return (
    <Dialog open={open} onClose={onClose} classes={{ paper: classes.paper }}>
      <div className={classes.header}>
        <Typography className={classes.title}>Avatar &amp; Color</Typography>
        <IconButton size="small" onClick={onClose} aria-label="Close">
          <CloseIcon fontSize="small" />
        </IconButton>
      </div>

      <div className={classes.body}>
        <div className={classes.preview}>
          <img
            src={builtinAvatarDataUrl(avatarId, hex)}
            alt="Avatar preview"
            width={42}
            height={42}
          />
          <Typography
            className={classes.previewName}
            style={{ color: `#${hex}` }}
          >
            {username}
          </Typography>
        </div>

        <Typography className={classes.sectionLabel}>Avatar</Typography>
        <div className={classes.avatarGrid}>
          {avatarIds.map((id) => (
            <button
              key={id}
              type="button"
              className={cx(
                classes.avatarTile,
                id === avatarId && classes.avatarTileSelected
              )}
              onClick={() => setAvatarId(id)}
              aria-label={`Avatar ${id}`}
              aria-pressed={id === avatarId}
            >
              <img
                src={builtinAvatarDataUrl(id, hex)}
                alt=""
                width={42}
                height={42}
              />
            </button>
          ))}
        </div>

        <Typography className={classes.sectionLabel}>Color</Typography>
        <div className={classes.colorGrid}>
          {swatchRows.map((c) => {
            const norm = normalizeBuiltinColor(c);
            return (
              <button
                key={c}
                type="button"
                className={cx(
                  classes.swatch,
                  norm === hex && classes.swatchSelected
                )}
                style={{ backgroundColor: c }}
                onClick={() => pickColor(c)}
                aria-label={`Color ${c}`}
                aria-pressed={norm === hex}
              />
            );
          })}
        </div>

        <div className={classes.hexRow}>
          <Typography className={classes.hexLabel}>Hex #</Typography>
          <TextField
            className={classes.hexInput}
            variant="outlined"
            size="small"
            value={hexDraft}
            onChange={(e) => handleHexInput(e.target.value)}
            slotProps={{ htmlInput: { maxLength: 6, spellCheck: false } }}
          />
          {hexError && (
            <Typography className={classes.hexError}>{hexError}</Typography>
          )}
        </div>
      </div>

      <div className={classes.actions}>
        <Button variant="outlined" size="small" onClick={onClose}>
          Cancel
        </Button>
        <Button
          className={classes.saveBtn}
          variant="contained"
          size="small"
          disabled={saving}
          onClick={handleSave}
        >
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </div>
    </Dialog>
  );
};

export default AvatarPickerDialog;
